import { ChangeDetectorRef, Component } from '@angular/core';
import { ErrorMessages } from '@constants/constants';
import { paginationParams } from '@shared/config/table.config';
import { Contractors, TabLabels, ContractorApiResponse } from '@interfaces/vendor-management-model';
import { columnDefs, StaticTexts, tabsData } from './contractors-org-view.config';
import { SearchService } from '@services/search.service';
import { IEmeraldTabData } from '@emerald/nxcore';
import { ApiStatus, PageTitle, PersonaType } from '@constants/global.enum';
import { DataService } from '@services/hiring-manger-profiledata.service';
import { AuthService } from '@core/auth.service';
import { GlobalErrorService } from '@services/global-error.service';
import { ActivatedRoute, Router } from '@angular/router';
import { month } from '../contractor-head-count/contractor-head-count.config';
import { EmployeeData } from '@app/shared/interfaces/hiring-manager.model';

@Component({
  selector: 'app-contractors-org-view',
  templateUrl: './contractors-org-view.component.html',
  styleUrl: './contractors-org-view.component.scss',
})
export class ContractorsOrgViewComponent {
  displayData: Contractors[] = [];
  paginationParams = paginationParams;
  columnDefs = columnDefs;
  rowData: Contractors[] = [];
  tabsData = tabsData;
  selectedTab!: IEmeraldTabData;
  tabLabel = TabLabels;
  filterValue: string = '';
  totalCount: number = 0;
  imageSrc: string = 'assets/img/no-data-found.png';
  errorMsg: string = ErrorMessages.NoDataAvailable;
  isDataLoaded: boolean = true;
  fieldsVisibility: string = PersonaType.ContractorOrgView;
  opened: boolean = false;
  employeeId: string | null = null;
  contractorId: string = '';
  pageTitleEnum = PageTitle;
  currentMonth = new Date().getMonth() + 1;
  currentYear = new Date().getFullYear();
  selectedMonth: number = this.currentMonth;
  selectedYear: number = this.currentYear;
  showContractorProfile: boolean = false;
  selectedContractor: Contractors | null = null;

  constructor(
    private searchService: SearchService,
    private cdr: ChangeDetectorRef,
    private dataService: DataService,
    private authService: AuthService,
    private globalError: GlobalErrorService,
    private router: Router,
    private route: ActivatedRoute, // Inject ActivatedRoute to access query parameters
  ) {}

  private showError(message: string): void {
    alert(message);
  }
 ngOnInit(): void {
  this.selectedTab = this.tabsData[0];

    this.route.queryParams.subscribe((params) => {
    const month = params['month'] ? Number(params['month']) : new Date().getMonth() + 1; // Default to current month
    const year = params['year'] ? Number(params['year']) : new Date().getFullYear();   // Default to current year

    this.authService.getEmployeeDetails()
      .then((employeeData: EmployeeData | null | undefined) => {
        this.employeeId = employeeData?.employeeId || null;

        if (this.employeeId) {
          this.selectedMonth = month;
          this.selectedYear = year;
          this.loadContractorsData(this.employeeId, this.selectedMonth, this.selectedYear); // Pass selected month and year
        } else {
          this.handleNoData();
        }
      })
      .catch((error) => {
        this.handleNoData();
        this.globalError.logComponentError(error);
      });
  });
}


private handleNoData() {
  this.isDataLoaded = true;
  this.errorMsg = StaticTexts.ErrorMessages.NoDataAvailable;
  this.displayData = [];
  this.rowData = [];
  this.totalCount = 0;
}

  loadContractorsData(employeeId: string, month: number, year: number): void {
    this.isDataLoaded = false;

    this.dataService.getContractors<ContractorApiResponse>(employeeId, month, year).subscribe({
      next: (response) => {
        if (response.status === ApiStatus.SUCCESS) {
          this.rowData = response.data.map((contractor) => this.mapContractorData(contractor));
          this.displayData = [...this.rowData];
          this.totalCount = this.rowData?.length;
        } else {
          this.errorMsg = response.reason || StaticTexts.ErrorMessages.NoDataAvailable;
          this.handleNoData();
        }
        this.isDataLoaded = true;
      },
      error: () => {
        this.errorMsg = StaticTexts.ErrorMessages.NoDataAvailable;
        this.handleNoData();
        this.isDataLoaded = true;
      },
    });
  }

mapContractorData(contractor: ContractorApiResponse): Contractors {
  return {
    contractorName: contractor.fullName || '',
    contractorId: contractor.employeeId || '',
    designation: contractor.jobTitle || '',
    reportingManager: contractor.reportingManager || '',
    status: contractor.status || '',
    contractEndDate: contractor.contractEndDate || '',
    primarySkill: contractor.primarySkills || '',
    dateOfJoining: contractor.lastHireDate || '',
    vendorName: contractor.employer || '',
    location: contractor.city || '',
    totalExperience: contractor.totalExperience || '',
    rto: contractor.currentMonthAttendanceDays !== null && contractor.currentMonthAttendanceDays !== undefined
      ? `${contractor.currentMonthAttendanceDays} days`
      : '',
  };
}
  searchContractors(event: KeyboardEvent) {
    const input = (event.target as HTMLInputElement).value;
    this.filterValue = input;

    if (this.selectedTab?.label === this.tabLabel.orgView) {
      this.searchContractorsByOrg(input, event as KeyboardEvent);
    }
  }

  searchContractorsByOrg(input: string, event: KeyboardEvent) {
    if (this.selectedTab?.label !== this.tabLabel.orgView) {
      return;
    }
    this.filterValue = this.searchService.getFilteredValue(
      event as KeyboardEvent,
    );
    this.displayData = this.rowData?.length
      ? this.searchService.getSearchedData(
          this.rowData,
          this.filterValue?.toLowerCase(),
        )
      : [];
    this.totalCount = this.displayData?.length;
  }

  clearInput() {
    this.displayData = this.rowData;
    this.totalCount = this.displayData?.length;
    this.filterValue = '';
  }

  onTabChange(tab: IEmeraldTabData) {
    this.selectedTab = tab;
    this.filterValue = '';
    if (this.selectedTab?.label === this.tabLabel.orgView) {
      this.displayData = this.rowData;
      this.totalCount = this.displayData?.length;
    }
  }

  navigateToContractorProfile(contractorId: string | number) {
     this.contractorId = contractorId?.toString();
    this.showContractorProfile = !!this.contractorId;
  }

  onManagerViewTotalCount(count: number) {
    this.totalCount = count;
    this.cdr.detectChanges();
  }

 onFilterApplied(filtered: Contractors[]) {
  this.displayData = filtered;
  this.totalCount = filtered.length;
}

  navigateToDashboard() {
    this.router.navigate(['/vendor-management']);
  }
}
